<template>
  <div
    id="menububble"
    ref="menuRef"
    class="flex absolute h-38 max-w-calc-50 z-9999 mb-8 menububble"
    :class="[{ 'is-active': showToolbar }, commentIsActive ? '-z-1' : 'z-9999']"
    :style="menuPosition"
  >
    <div
      class="no-scroll-bar rounded-4 z-20 shadow-menu overflow-x-auto flex opacity-90 hover:opacity-100"
      :class="isDarkMode ? 'bg-sidebar' : 'bg-primary'"
    >
      <MenuButton
        class="text-12 font-black"
        :is-active="editor.isActive('bold')"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <i class="icon-bold align-middle" />
      </MenuButton>

      <MenuButton
        class="text-12 font-black italic"
        :is-active="editor.isActive('italic')"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <i class="icon-italic align-middle" />
      </MenuButton>

      <MenuButton
        class="text-12 border-r border-hover font-black"
        :is-active="editor.isActive('underline')"
        @click="editor.chain().focus().toggleUnderline().run()"
      >
        <i class="icon-underline align-middle font-bold" />
      </MenuButton>

      <MenuButton
        class="text-13"
        :is-active="editor.isActive('heading', { level: 1 })"
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
      >
        H1
      </MenuButton>

      <MenuButton
        class="text-13"
        :is-active="editor.isActive('heading', { level: 2 })"
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      >
        H2
      </MenuButton>

      <MenuButton
        class="text-13 border-r border-hover"
        :is-active="editor.isActive('heading', { level: 3 })"
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
      >
        H3
      </MenuButton>

      <MenuButton
        ref="linkBtnRef"
        class="flex justify-center items-center pt-1"
        :is-active="editor.isActive('link') || linkMenuIsActive"
        @click="toggleLinkMenu"
      >
        <i class="icon-link text-15" />
      </MenuButton>

      <MenuButton
        v-if="!isEvent"
        class="border-r border-hover pb-2"
        :is-active="editor.isActive('commentMark') || commentIsActive"
        @click="showComment"
      >
        <i class="icon-chat align-text-bottom text-15" />
      </MenuButton>

      <MenuButton
        ref="textColorBtnRef"
        class="border-r border-hover flex justify-center items-center relative"
        :is-active="editor.isActive('textColor') || textColorIsActive"
        @click="showTextColor"
      >
        <i class="icon-format-color-text" />
      </MenuButton>

      <MenuButton
        ref="backgroundBtnRef"
        class="flex justify-center items-center relative"
        :is-active="editor.isActive('highlight') || backgroundColorIsActive"
        @click="showBackgroundColor"
      >
        <i class="icon-highlight text-16" />
      </MenuButton>
    </div>

    <form
      v-show="linkMenuIsActive"
      ref="linkRef"
      class="flex items-center absolute z-9999 shadow-dropdown"
      :style="linkPosition"
      @submit.prevent="toggleLinkUrl(linkUrl)"
    >
      <input
        ref="linkInputRef"
        v-model="linkUrl"
        class="py-8 px-20 outline-none lowercase h-40 bg-primary text-app-color"
        type="text"
        placeholder="https://"
        @keydown.esc="hideLinkMenu"
      >
      <button v-if="editor.isActive('link')" class="text-13 text-grey-300 w-40 h-40 bg-primary rounded-r-4" type="button" @click="toggleLinkUrl(null)">
        <i class="icon-close" />
      </button>
    </form>

    <portal to="editor">
      <CommentForm
        v-if="commentIsActive"
        ref="commentRef"
        :key="project.id"
        :comment-id="comment"
        :comment-position="commentPosition"
        @setComment="setComment"
        @hideComment="hideComment"
      />
    </portal>

    <div v-show="textColorIsActive" ref="textColorRef" class="absolute z-10 bg-primary rounded-10 shadow w-235" :style="textColorPosition">
      <div class="cursor-default">
        <p class="tex-16 font-bold text-left text-app-color pl-10 pt-5">Pick a color</p>
        <div class="w-full flex flex-wrap m-5">
          <div
            v-for="(color, index) in colors.slice(0, 15)"
            :key="color + index"
            class="w-37 h-37 rounded-full mx-4 my-5 cursor-pointer flex"
            :style="`background-color: ${color}`"
            @click.stop="applyTextColor(color)"
          >
            <i v-if="activeTextColor === color" class="icon-check mx-auto mt-6 text-22 text-white font-bold" />
          </div>
        </div>
        <div class="p-10 pt-0" @click.stop>
          <input
            v-model="textColor"
            type="text"
            class="text-app-color w-full px-16 py-8 rounded-4"
            :class="isDarkMode ? 'bg-hover' : 'bg-sidebar'"
            placeholder="#0033A0"
            @click.stop @keypress.enter="applyTextColor(textColor)"
          >
        </div>
        <div
          v-if="editor.isActive('textColor')"
          class="flex items-center p-10 pt-0 text-12 text-app-color opacity-50
                 hover:opacity-100 cursor-pointer transition duration-300"
          @click.stop="removeTextColor"
        >
          <i class="icon-remove mr-5" />
          <p>Remove color</p>
        </div>
      </div>
    </div>

    <div
      v-show="backgroundColorIsActive"
      ref="backgroundRef"
      class="absolute z-10 bg-primary rounded-10 shadow w-235"
      :style="backgroundColorPosition"
    >
      <div class="cursor-default">
        <p class="tex-16 font-bold text-left text-app-color pl-10 pt-5">Pick a fill color</p>
        <div class="w-full flex flex-wrap m-5">
          <div
            v-for="(color, index) in backgroundColors.slice(0, 10)"
            :key="color + index"
            class="w-37 h-37 rounded-full mx-4 my-5 cursor-pointer flex"
            :style="`background-color: ${color}`"
            @click.stop="applyBackgroundColor(color)"
          >
            <i v-if="activeBackgroundColor === color" class="icon-check mx-auto mt-6 text-22 text-white font-bold" />
          </div>
        </div>
        <div class="p-10 pt-0">
          <input
            v-model="backgroundColor"
            type="text"
            class="text-app-color w-full px-16 py-8 rounded-4"
            :class="isDarkMode ? 'bg-hover' : 'bg-sidebar'"
            placeholder="#0033A0"
            @click.stop @keypress.enter="applyBackgroundColor(backgroundColor)"
          >
        </div>
        <div
          v-if="editor.isActive('highlight')"
          class="flex items-center p-10 pt-0 text-12 text-app-color opacity-50
                 hover:opacity-100 cursor-pointer transition duration-300"
          @click.stop="removeBackgroundColor"
        >
          <i class="icon-remove mr-5" />
          <p>Remove color</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import CommentForm from '@/components/comments/CommentForm.vue'
import MenuButton from '@/components/workspace/MenuButton.vue'

import { getPosition, rgbToHex } from '@/core/helper-functions'
import { getMarkAttrs } from '@/tiptap/utils/getAttrs'
import MenuBubble from '@/tiptap/plugins/MenuBubble'
import { commentService } from '@/services'
import defaultColors from '@/core/colors'

export default defineComponent({
  components: { CommentForm, MenuButton },

  props: {
    editor: Object,
    project: Object,
    isTableActive: Boolean,
    isEvent: { type: Boolean, default: false }
  },

  emits: ['isActive'],

  setup (props, { emit }) {
    const { state, getters, commit } = useStore()
    const route = useRoute()

    const colors = defaultColors.colors
    const backgroundColors = defaultColors.backgroundColors

    const linkRef = ref(null)
    const linkBtnRef = ref(null)
    const linkInputRef = ref(null)
    const linkUrl = ref(null)
    const linkPosition = ref('')
    const linkMenuIsActive = ref(false)

    const comment = ref(null)
    const commentPosition = ref('top: -100%; left: -100%')
    const commentIsActive = ref(false)
    const commentRef = ref(null)
    const commentInputRef = ref(null)

    const textColorRef = ref(null)
    const textColorBtnRef = ref(null)
    const textColor = ref(null)
    const textColorPosition = ref({})
    const activeTextColor = ref(false)
    const textColorIsActive = ref(false)

    const backgroundRef = ref(null)
    const backgroundBtnRef = ref(null)
    const backgroundColor = ref(null)
    const backgroundColorPosition = ref({})
    const activeBackgroundColor = ref(null)
    const backgroundColorIsActive = ref(false)

    const isActive = ref(false)

    const menu = ref({})
    const menuRef = ref(null)

    const editor = computed(() => props.editor)
    const comments = computed(() => props.project.comments)

    const user = computed(() => state.profile.user)
    const share = computed(() => state.project.share)
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isMagenta = computed(() => getters.isMagenta)

    const menuPosition = computed(() => {
      const left = isMobile.value ? 'left: 50%' : `left: ${menu.value.left - 190}px`
      const bottom = menu.value.top < 80 && menu.value.bottom > 85 ? menu.value.bottom - 85 : isMobile.value ? menu.value.bottom + 15 : menu.value.bottom
      return `${left}; bottom: ${bottom}px`
    })
    const showToolbar = computed(() => {
      return isActive.value && (props.editor.isFocused || commentIsActive.value || textColorIsActive.value || backgroundColorIsActive.value || linkMenuIsActive.value)
    })

    const userName = computed(() => user?.value?.profile.firstName || 'Guest')

    watch(editor, () => {
      nextTick(() => {
        editor.value.registerPlugin(MenuBubble({
          editor: editor.value,
          element: menuRef.value,

          onUpdate: data => {
            isActive.value = data.isActive
            menu.value = data
          }
        }))
      })
    }, { immediate: true })

    watch(isActive, (value) => {
      emit('isActive', value)

      if (value && props.editor.isActive('link')) showLinkForm()

      if (!value) {
        comment.value = ''
        reset()
      }
    })

    function showLinkForm () {
      reset('link')
      const attrs = getMarkAttrs(props.editor.state, 'link')

      linkUrl.value = attrs.href
      linkMenuIsActive.value = !linkMenuIsActive.value

      nextTick(() => {
        linkPosition.value = getPosition(menuRef.value, linkRef.value, linkBtnRef.value.$el, true)
      })
    }

    function toggleLinkMenu () {
      showLinkForm()
      nextTick(() => linkInputRef.value?.focus())
    }

    function hideLinkMenu () {
      linkUrl.value = 'https://'
      linkMenuIsActive.value = false
    }

    function parseUrl (url) {
      const splitUrl = url.split('://')
      if (splitUrl.length > 1) return url

      return `https://${url}`
    }

    function toggleLinkUrl (url) {
      if (url) {
        const href = parseUrl(url)
        props.editor.chain().focus().setLink({ href: href }).run()
      } else {
        props.editor.chain().focus().unsetLink().run()
      }

      hideLinkMenu()
    }

    function showComment () {
      reset('comment')

      comment.value = getMarkAttrs(props.editor.state, 'commentMark').commentid
      commentIsActive.value = !commentIsActive.value

      getCommentPosition()

      if (commentIsActive.value) {
        setTimeout(() => {
          commentRef.value.setFocus()
        }, 100)
      }
    }

    function getCommentPosition () {
      const editorWrapper = document.getElementById('editor-wrapper')
      const editorRect = editorWrapper?.getBoundingClientRect()
      const pos = props.editor.state.selection.$from.pos
      const coords = props.editor.view.coordsAtPos(pos)
      const commentWidth = 270

      let left = coords.left - editorRect.x

      if (coords.left + commentWidth > window.innerWidth) {
        left = editorRect.right - commentWidth - editorRect.x
      }

      commentPosition.value = `left: ${left}px; top: ${coords.bottom - editorRect.y}px`
    }

    function hideComment () {
      comment.value = null
      commentIsActive.value = false
    }

    function setComment (value) {
      commit('loading', true)

      const params = getMarkAttrs(props.editor.state, 'commentMark')

      if (value && !params?.commentid) {
        createComment(value)
      } else if (value && params?.commentid) {
        updateComment(value, params)
      } else if (params?.commentid) {
        deleteComment(value, params)
      } else {
        commit('loading', false)
      }

      comment.value = ''
      hideComment()
    }

    function createComment (value) {
      if (route.name === 'Share') {
        commentService.createWithShare({ body: value }, props.project.id, share.value.id)
          .then(({ data }) => {
            const arr = comments.value

            arr.push(data.dataValues)
            commit('updateComments', arr)

            props.editor.chain().focus().addCommentMark({
              comment: value,
              commentid: data.dataValues.id
            }).run()

            props.editor.chain().focus().addCommentNode({
              content: value,
              contentid: data.dataValues.id
            }).run()
          })
      } else {
        commentService.create({ body: value }, props.project.id)
          .then(({ data }) => {
            const arr = comments.value
            const newComment = {
              ...data.dataValues,
              user: user.value
            }

            arr.push(newComment)
            commit('updateComments', arr)

            props.editor.chain().focus().addCommentMark({
              comment: value,
              commentid: data.dataValues.id
            }).run()

            props.editor.chain().focus().addCommentNode({
              content: value,
              contentid: data.dataValues.id
            }).run()
          })
      }
    }

    function updateComment (value, params) {
      commentService.update(params.commentid, value)
        .then(() => {
          props.editor.chain().focus().addCommentMark({
            comment: value,
            commentid: params.commentid
          }).run()
          const comment = comments.value.find(item => item.id === +params.commentid)
          comment.body = value
          commit('updateComments', comments)
        })
    }

    function deleteComment (value, params) {
      commentService.delete(params.commentid)

      commit('updateComments', comments.value.filter(item => item.id !== params.commentid))

      props.editor.chain().removeCommentMark().run()
      props.editor.chain().focus().addCommentNode({
        content: value,
        contentid: params.commentid,
        commentid: params.commentid
      }).run()
    }

    function applyTextColor (color) {
      textColorIsActive.value = false
      textColor.value = ''
      const textcolor = getMarkAttrs(props.editor.state, 'textColor').color?.split(/\s/).join('')

      textcolor === color
        ? props.editor.chain().focus().unsetTextColor().run()
        : props.editor.chain().focus().setTextColor({ color }).run()
    }

    function applyBackgroundColor (color) {
      backgroundColorIsActive.value = false
      backgroundColor.value = ''
      const bgColor = getMarkAttrs(props.editor.state, 'highlight').color?.split(/\s/).join('')

      bgColor === color
        ? props.editor.chain().focus().unsetHighlight().run()
        : props.editor.chain().focus().setHighlight({ color: color }).run()
    }

    function showTextColor () {
      reset('textColor')

      textColorIsActive.value = !textColorIsActive.value

      nextTick(() => {
        textColorPosition.value = getPosition(menuRef.value, textColorRef.value, textColorBtnRef.value.$el)
      })

      const color = getMarkAttrs(props.editor.state, 'textColor').color?.split(/\s/).join('')

      if (colors.every(item => item !== color)) {
        textColor.value = color
        activeTextColor.value = ''
      } else {
        textColor.value = ''
        activeTextColor.value = color
      }
    }

    function showBackgroundColor () {
      reset('backgroundColor')

      backgroundColorIsActive.value = !backgroundColorIsActive.value

      nextTick(() => {
        backgroundColorPosition.value = getPosition(menuRef.value, backgroundRef.value, backgroundBtnRef.value.$el)
      })

      const bgColor = getMarkAttrs(props.editor.state, 'highlight').color?.split(/\s/).join('')

      if (backgroundColors.every(item => item !== bgColor)) {
        backgroundColor.value = rgbToHex(bgColor)
        activeBackgroundColor.value = ''
      } else {
        backgroundColor.value = ''
        activeBackgroundColor.value = bgColor
      }
    }

    function removeBackgroundColor () {
      backgroundColorIsActive.value = false
      backgroundColor.value = ''
      props.editor.chain().focus().unsetHighlight().run()
    }

    function removeTextColor (command) {
      textColorIsActive.value = false
      textColor.value = ''
      props.editor.chain().focus().unsetTextColor().run()
    }

    function reset (exclude) {
      if (exclude !== 'comment') commentIsActive.value = false
      if (exclude !== 'link') linkMenuIsActive.value = false
      if (exclude !== 'textColor') textColorIsActive.value = false
      if (exclude !== 'backgroundColor') backgroundColorIsActive.value = false
    }

    return {
      // data
      linkUrl,
      linkPosition,
      linkMenuIsActive,
      comment,
      commentPosition,
      commentIsActive,
      textColor,
      textColorPosition,
      activeTextColor,
      textColorIsActive,
      backgroundColor,
      backgroundColorPosition,
      activeBackgroundColor,
      backgroundColorIsActive,
      backgroundColors,
      colors,
      // refs
      menuRef,
      backgroundRef,
      backgroundBtnRef,
      textColorRef,
      textColorBtnRef,
      linkRef,
      linkBtnRef,
      linkInputRef,
      commentRef,
      commentInputRef,
      // computed
      isMagenta,
      isDarkMode,
      menuPosition,
      showToolbar,
      userName,
      // functions
      toggleLinkMenu,
      hideLinkMenu,
      toggleLinkUrl,
      showComment,
      hideComment,
      setComment,
      applyTextColor,
      applyBackgroundColor,
      showTextColor,
      showBackgroundColor,
      removeBackgroundColor,
      removeTextColor
    }
  }
})
</script>
